
import {hash32, truncate} from './util';

type Emit = (name: string, props: Record<string, unknown>) => void;

const INTERACTIVE = 'a, button, input, select, textarea, label, summary, [role="button"], [role="link"], [data-obs-name]';
const MOVE_SAMPLE_MS = 250;
const MAX_MOVE_POINTS = 400;
const RAGE_CLICKS = 3;
const RAGE_WINDOW_MS = 600;
const RAGE_RADIUS_PX = 30;
const MAX_ERRORS_PER_PAGE = 10;

const round = (n: number) => Math.round(n * 1000) / 1000;

const docSize = () => ({
	w: Math.max(document.documentElement.scrollWidth, window.innerWidth, 1),
	h: Math.max(document.documentElement.scrollHeight, window.innerHeight, 1),
});

// CSS-module class names carry a build hash suffix; keep the readable part
const cleanClass = (c: string) => c.replace(/--[A-Za-z0-9_-]{5}$/, '');

const describe = (el: Element): string => {
	let part = el.tagName.toLowerCase();
	if (el.id && !/\d{3,}/.test(el.id)) {
		return `${part}#${el.id}`;
	}
	const cls = Array.from(el.classList).slice(0, 2).map(cleanClass);
	if (cls.length) {
		part += '.' + cls.join('.');
	}
	return part;
};

// Short ancestor path, for grouping clicks on the same element
export const selectorFor = (el: Element): string => {
	const parts: string[] = [];
	for (let cur: Element | null = el; cur && parts.length < 4 && cur !== document.body; cur = cur.parentElement) {
		parts.unshift(describe(cur));
		if (cur.id) {
			break;
		}
	}
	return truncate(parts.join(' > '), 200) || '';
};

// Element text is never sent: only a hash, so clicks on "Book now" group
// together without shipping whatever text a user's own content contains
const textHash = (el: Element): string | undefined => {
	const text = (el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 64);
	return text ? hash32(text).toString(36) : undefined;
};

export class AutoCapture {
	private clicks: {t: number, x: number, y: number}[] = [];
	private moves: [number, number][] = [];
	private lastMove: {x: number, y: number} | null = null;
	private moveTimer: ReturnType<typeof setInterval> | null = null;
	private maxDepth = 0;
	private errors = 0;
	private listeners: [EventTarget, string, EventListener][] = [];

	constructor(private emit: Emit) {}

	start() {
		this.on(document, 'click', (e) => this.onClick(e as MouseEvent));
		this.on(window, 'scroll', () => this.onScroll());
		this.on(window, 'mousemove', (e) => {
			const m = e as MouseEvent;
			this.lastMove = {x: m.pageX, y: m.pageY};
		});
		this.on(window, 'error', (e) => this.onError(e as ErrorEvent));
		this.on(window, 'unhandledrejection', (e) => this.onRejection(e as PromiseRejectionEvent));
		this.moveTimer = setInterval(() => this.sampleMove(), MOVE_SAMPLE_MS);
		this.onScroll();
	}

	stop() {
		for (const [target, type, fn] of this.listeners) {
			target.removeEventListener(type, fn, true);
		}
		this.listeners = [];
		if (this.moveTimer) {
			clearInterval(this.moveTimer);
			this.moveTimer = null;
		}
	}

	// Called as a page view ends: emit its scroll depth and movement summary
	endPage() {
		if (this.maxDepth > 0) {
			this.emit('$scroll', {depth: Math.round(this.maxDepth * 100)});
		}
		if (this.moves.length) {
			const {w, h} = docSize();
			this.emit('$move', {
				points: this.moves,
				vw: window.innerWidth,
				vh: window.innerHeight,
				dw: w,
				dh: h,
			});
		}
		this.maxDepth = 0;
		this.moves = [];
		this.errors = 0;
		this.clicks = [];
		// Depth for the next page starts from wherever its viewport sits
		this.onScroll();
	}

	private on(target: EventTarget, type: string, fn: EventListener) {
		target.addEventListener(type, fn, {capture: true, passive: true});
		this.listeners.push([target, type, fn]);
	}

	private onClick(e: MouseEvent) {
		const target = e.target instanceof Element ? e.target : null;
		if (!target) {
			return;
		}
		const el = target.closest(INTERACTIVE) || target;
		const {w, h} = docSize();
		const x = round(e.pageX / w);
		const y = round(e.pageY / h);
		const selector = selectorFor(el);
		const props: Record<string, unknown> = {
			x, y,
			vw: window.innerWidth,
			vh: window.innerHeight,
			selector,
			tag: el.tagName.toLowerCase(),
		};
		const name = el.getAttribute('data-obs-name');
		if (name) {
			props.name = truncate(name, 64);
		}
		const th = textHash(el);
		if (th) {
			props.text_hash = th;
		}
		this.emit('$click', props);

		const now = Date.now();
		this.clicks = this.clicks
			.filter((c) => now - c.t < RAGE_WINDOW_MS && Math.hypot(c.x - e.pageX, c.y - e.pageY) < RAGE_RADIUS_PX)
			.concat({t: now, x: e.pageX, y: e.pageY});
		if (this.clicks.length === RAGE_CLICKS) {
			this.emit('$rage_click', {x, y, selector});
		}
	}

	private onScroll() {
		const {h} = docSize();
		const depth = Math.min(1, (window.scrollY + window.innerHeight) / h);
		if (depth > this.maxDepth) {
			this.maxDepth = depth;
		}
	}

	private sampleMove() {
		if (!this.lastMove || this.moves.length >= MAX_MOVE_POINTS) {
			return;
		}
		const {w, h} = docSize();
		this.moves.push([round(this.lastMove.x / w), round(this.lastMove.y / h)]);
		this.lastMove = null;
	}

	private onError(e: ErrorEvent) {
		// Resource load failures (img/script 404s) carry no message: noise
		if (!e.message || this.errors >= MAX_ERRORS_PER_PAGE) {
			return;
		}
		this.errors++;
		this.emit('$error', {
			message: truncate(e.message, 500),
			source: truncate(e.filename, 300),
			line: e.lineno,
			col: e.colno,
			stack: truncate(e.error instanceof Error ? e.error.stack : undefined, 2000),
		});
	}

	private onRejection(e: PromiseRejectionEvent) {
		if (this.errors >= MAX_ERRORS_PER_PAGE) {
			return;
		}
		this.errors++;
		const reason = e.reason;
		this.emit('$error', {
			message: truncate(reason instanceof Error ? reason.message : String(reason), 500),
			stack: truncate(reason instanceof Error ? reason.stack : undefined, 2000),
			source: 'unhandledrejection',
		});
	}
}
